import { useEffect } from "react";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { getPortSide } from "~/lib/getPortSide";
import { parsePortSide } from "~/lib/parsePortSide";
import {
  setBinIsPresent,
  setLeftOrRightPortActive,
  GetPortResponse,
  setPortStatus
} from "~/redux/actions";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";

import { PortSide } from "~/types/api";

import { usePortStatusQuery } from "./usePortStatus";

type UsePortStatusWithReduxUpdatesProps = {
  portId: number;
};

export function usePortStatusWithReduxUpdates(
  props: UsePortStatusWithReduxUpdatesProps
): ReturnType<typeof usePortStatusQuery> {
  const { portId } = props;
  const dispatch = useAppDispatch();
  const workstation = useAppSelector(selectThisWorkstation);
  const portStatus = usePortStatusQuery(portId);

  const portType = portStatus.portType;
  const mode = portStatus.mode;
  const isReady = portStatus.isReady;
  const isBinOpen = portStatus.isBinOpen;
  const selectedBin = portStatus.selectedBin;
  const selectedTask = portStatus.selectedTask;
  const binId = portStatus.selectedBin;
  const xPosition = portStatus.binState?.binState.xPosition;
  const yPosition = portStatus.binState?.binState.yPosition;
  const portSide = portStatus.binState?.portSide;

  useEffect(() => {
    if (
      portType &&
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      portId !== undefined &&
      mode &&
      isReady !== undefined &&
      selectedBin !== undefined &&
      selectedTask !== undefined
    ) {
      const getPortResponse: GetPortResponse = {
        portType,
        portId,
        mode,
        isReady,
        selectedBin,
        selectedTask
      };
      dispatch(setPortStatus(getPortResponse));
    }
  }, [dispatch, portType, portId, mode, isReady, selectedBin, selectedTask]);

  useEffect(() => {
    const binPresent = isReady || isBinOpen;
    if (isReady !== undefined) {
      dispatch(
        setBinIsPresent({ status: binPresent, binId, reporter: "portStatus" })
      );
    }
  }, [dispatch, isReady, isBinOpen, binId]);

  useEffect(() => {
    const portReady = isReady || isBinOpen;
    const portSideFromCoordinates: PortSide =
      workstation &&
      xPosition !== undefined &&
      yPosition !== undefined &&
      portReady
        ? getPortSide(workstation, { x: xPosition, y: yPosition })
        : "Unknown";

    const portSideFromBinState = parsePortSide(portSide);

    const portSideCoalesced =
      portSideFromBinState === "Unknown"
        ? portSideFromCoordinates
        : portSideFromBinState;

    dispatch(setLeftOrRightPortActive(portSideCoalesced));
  }, [
    dispatch,
    portSide,
    workstation,
    xPosition,
    yPosition,
    isReady,
    isBinOpen
  ]);

  return portStatus;
}
